import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import MetaTags from "react-meta-tags";
import { Row, Col, Title, SubTitle, Text, Button } from "../theme";
import BlockContent from "@sanity/block-content-to-react";
import MemeViewer from "./memeViewer";
import { useParams } from "react-router-dom";

const Editions = () => {
  const [settings, editions, memes] = useSelector((state) => [
    state.data.settings,
    state.data.editions,
    state.data.memes,
  ]);
  const [selected, setSelected] = useState(null);
  const nextEdition = settings?.nextEdition;
  const { editionId } = useParams();

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const regex =
      /(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const sortedEditions = [...(editions || [])].sort(
    (a, b) => parseInt(a.edition) - parseInt(b.edition),
  );
  const editionTitles = sortedEditions.map((ed) => ed.edition);
  const latestEdition = sortedEditions[sortedEditions.length - 1];

  useEffect(() => {
    if (sortedEditions.length > 0 && !selected) {
      const match = sortedEditions.find((e) => e.edition === editionId);
      setSelected(match || latestEdition);
    }
  }, [editionId, sortedEditions, selected]);

  const getMemesForSelectedEdition = () => {
    if (!memes || !selected) return [];
    return memes
      .filter((m) => m.display && m.edition === selected.edition)
      .map((m) => ({ _ref: m._id }));
  };

  if (!settings || !editions || !memes) return null;

  return (
    <>
      <MetaTags>
        <title>{`${settings?.title || "Editions"} - Editions`}</title>
        <meta
          name="description"
          content="Browse editions with their videos and info."
        />
      </MetaTags>

      {/* Upcoming MEME editions will be announced here, along with videos and memes from past editions. Stay tuned for the latest updates and content from MEME! */}
      {settings.live ? null : (
        <Row>
          <Col xs={12}>
            {nextEdition && (
              <Row>
                <Col xs={12}>
                  <Title>Upcoming: MEME {nextEdition.edition}</Title>
                  <Text>
                    <BlockContent blocks={nextEdition.description} />
                  </Text>
                  {nextEdition.youtubeLiveUrl && (
                    <div
                      style={{
                        position: "relative",
                        paddingBottom: "56.25%",
                        height: 0,
                        overflow: "hidden",
                        marginTop: "1rem",
                      }}
                    >
                      <iframe
                        src={getYouTubeEmbedUrl(nextEdition.youtubeLiveUrl)}
                        title="Upcoming Live Stream"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                      ></iframe>
                    </div>
                  )}
                  <br />
                  <hr />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      )}

      {settings.live ? null : (
        <Row>
          <Col xs={12}>
            <Title>Previous Editions</Title>
            <Row>
              {editionTitles.map((name, i) => (
                <Col xs={6} sm={4} md={3} key={i}>
                  <Button
                    text={name}
                    noUrl
                    invert={selected && selected.edition == name}
                    action={() =>
                      setSelected(
                        sortedEditions.find((e) => e.edition === name),
                      )
                    }
                  />
                </Col>
              ))}
            </Row>
            <br />
          </Col>
        </Row>
      )}

      {selected && (
        <Row>
          <Col xs={12}>
            <Title>{selected.edition}</Title>
            <Text>
              <BlockContent blocks={selected.description} />
            </Text>
            <br />
            <SubTitle>Videos</SubTitle>
            <br />
            <Row>
              {selected.videos && selected.videos.length > 0 ? (
                selected.videos.map((url, i) => (
                  <Col xs={12} sm={12} md={6} key={i}>
                    <div
                      style={{
                        position: "relative",
                        paddingBottom: "56.25%",
                        height: 0,
                        overflow: "hidden",
                        marginBottom: "1rem",
                      }}
                    >
                      <iframe
                        src={getYouTubeEmbedUrl(url)}
                        title={`video-${i}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                      ></iframe>
                    </div>
                  </Col>
                ))
              ) : (
                <Text>No videos available for this edition.</Text>
              )}
            </Row>

            {!settings.live ? null : (
              <Row>
                <Col xs={12}>
                  <Title>Previous Editions</Title>
                  <Row>
                    {editionTitles.map((name, i) => (
                      <Col xs={6} sm={4} md={3} key={i}>
                        <Button
                          text={name}
                          noUrl
                          invert={selected && selected.edition == name}
                          action={() =>
                            setSelected(
                              sortedEditions.find((e) => e.edition === name),
                            )
                          }
                        />
                      </Col>
                    ))}
                  </Row>
                  <br />
                </Col>
              </Row>
            )}

            {selected.images && selected.images.length > 0 && (
              <>
                <br />
                <br />
                <Row>
                  {selected.images.map((img, i) => (
                    <Col xs={12} sm={6} md={4} key={i}>
                      <img
                        src={img.img}
                        alt={img.description || `Edition image ${i + 1}`}
                        style={{
                          width: "100%",
                          borderRadius: "8px",
                          marginBottom: "0.5rem",
                        }}
                      />
                      {img.description && (
                        <Text
                          style={{ fontStyle: "italic", fontSize: "0.9rem" }}
                        >
                          <i>
                            <BlockContent blocks={img.description} />
                          </i>
                        </Text>
                      )}
                    </Col>
                  ))}
                </Row>
                <br />
              </>
            )}

            <br />
            <br />
            <Row>
              <Col xs={12} sm={12} md={7}>
                <SubTitle>Memes for this edition</SubTitle>
                <br />
                <MemeViewer
                  list={getMemesForSelectedEdition()}
                  amount={3}
                  auto
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </>
  );
};

export default Editions;
